import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';

import { HeaderComponent } from '@shared/components/header/header.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { ModuloEnum } from '@shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '@shared/enums/usuario/funcionalidade.enum';
import { TipoEtapaKanbanEnum } from '@shared/enums/servico/tipo-etapa-kanban.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { ConfigKanbanService } from '@shared/services/apis/servico/config-kanban.service';
import { CampoAdicionalService } from '@shared/services/apis/servico/campo-adicional.service';
import { AutomacaoOrdemServicoService } from '@shared/services/apis/servico/automacao-ordem-servico.service';
import { AutomacaoOrdemServicoConfig } from '@shared/services/models/servico/automacao-ordem-servico-config.model';
import { PerfilService } from '@shared/services/apis/usuario/perfil.service';
import { EtapaKanbanStruct } from '@shared/services/structs/servico/etapa-kanban.struct';
import { KanbanCor } from '@shared/services/models/servico/kanban-cor.model';
import { KanbanIcone } from '@shared/services/models/servico/kanban-icone.model';
import { CampoAdicionalStruct } from '@shared/services/structs/servico/campo-adicional.struct';
import { PerfilOptionStruct } from '@shared/services/structs/usuario/perfil-option.struct';

import {
  EtapaKanbanOrdemServicoModalComponent,
  EtapaKanbanOrdemServicoModalResult,
} from './dialogs/etapa-kanban-ordem-servico-modal/etapa-kanban-ordem-servico-modal.component';
import {
  AutomacaoDaEtapa,
  EtapaKanbanOrdemServicoExcluirDialogComponent,
  EtapaKanbanOrdemServicoExcluirDialogResult,
} from './dialogs/etapa-kanban-ordem-servico-excluir-dialog/etapa-kanban-ordem-servico-excluir-dialog.component';

const ROTA_CONFIG_SERVICOS = '/configuracoes/servicos';
const ROTA_CONFIG_GERAL_ORDEM_SERVICO = '/configuracoes/servicos/gerais';

interface AvisoFluxo {
  idEtapaKanban: number | null;
  mensagem: string;
}

@Component({
  selector: 'app-config-kanban-ordem-servico',
  standalone: true,
  imports: [
    CommonModule,
    DragDropModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatTooltipModule,
    HeaderComponent,
    LoadingBoxComponent,
  ],
  templateUrl: './config-kanban-ordem-servico.component.html',
  styleUrls: ['./config-kanban-ordem-servico.component.css'],
})
export class ConfigKanbanOrdemServicoComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Servicos;

  public isLoading = false;
  public isFalhaConfig = false;
  public etapas: EtapaKanbanStruct[] = [];
  public fluxo: EtapaKanbanStruct[] = [];
  public especiais: EtapaKanbanStruct[] = [];
  public avisos: AvisoFluxo[] = [];
  public cores: KanbanCor[] = [];
  public icones: KanbanIcone[] = [];
  public iconesById = new Map<number, string>();
  public coresById = new Map<number, KanbanCor>();
  public perfis: PerfilOptionStruct[] = [];
  public camposAdicionais: CampoAdicionalStruct[] = [];
  public automacoesPorEtapa = new Map<number, AutomacaoDaEtapa[]>();

  public readonly skeletonCards = Array(7);
  public mostrarAjuda = false;
  public avisosAbertos = true;
  public idEtapaProcessando: number | null = null;

  private rotaVoltar = ROTA_CONFIG_SERVICOS;
  private fixoSobrevoado: EtapaKanbanStruct | null = null;

  public podeSoltarNaPosicao = (index: number): boolean => {
    const fixo = this.fluxo[index];
    if (fixo?.isFixo) {
      this.fixoSobrevoado = fixo;
      return false;
    }
    return true;
  };

  constructor(
    private configKanbanService: ConfigKanbanService,
    private campoAdicionalService: CampoAdicionalService,
    private automacaoOrdemServicoService: AutomacaoOrdemServicoService,
    private perfilService: PerfilService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    if (this.route.snapshot.queryParamMap.get('origem') === 'gerais') {
      this.rotaVoltar = ROTA_CONFIG_GERAL_ORDEM_SERVICO;
    }
    this.carregarTudo();
  }

  private carregarTudo(): void {
    this.isLoading = true;
    forkJoin({
      config: this.configKanbanService.get().pipe(catchError(() => of(null))),
      lookups: this.configKanbanService.getLookups().pipe(catchError(() => of(null))),
      perfis: this.perfilService.getAllLookup().pipe(catchError(() => of(null))),
      campos: this.campoAdicionalService.getAll('', 0, 500).pipe(catchError(() => of(null))),
      automacoes: this.automacaoOrdemServicoService.get().pipe(catchError(() => of(null))),
    }).subscribe({
      next: res => {
        this.isLoading = false;
        if (res.lookups && !res.lookups.isError) {
          this.cores = res.lookups.cores || [];
          this.icones = res.lookups.icones || [];
          this.iconesById = new Map(this.icones.map(i => [i.idKanbanIcone, i.nomeIcone]));
          this.coresById = new Map(this.cores.map(c => [c.idKanbanCor, c]));
        } else {
          this.alertService.show(
            'Aviso',
            res.lookups?.errorDescription ||
              'Não foi possível carregar as cores e os ícones. Tente novamente em alguns instantes.',
            AlertType.warning
          );
        }
        if (res.perfis && !res.perfis.isError) {
          this.perfis = (res.perfis.listaPerfil || []).map(p => ({
            idPerfil: p.perfil.idPerfil,
            nome: p.perfil.nome,
          }));
        } else {
          this.alertService.show(
            'Aviso',
            res.perfis?.errorDescription ||
              'Não foi possível carregar os perfis agora. Tente novamente em alguns instantes.',
            AlertType.warning
          );
        }
        if (res.campos && !res.campos.isError) {
          this.camposAdicionais = res.campos.listaCamposAdicionais || [];
        } else {
          this.alertService.show(
            'Aviso',
            res.campos?.errorDescription ||
              'Não foi possível carregar os campos adicionais agora. Tente novamente em alguns instantes.',
            AlertType.warning
          );
        }
        if (res.automacoes && !res.automacoes.isError && res.automacoes.config) {
          this.aplicarAutomacoes(res.automacoes.config);
        }
        if (res.config && !res.config.isError) {
          this.aplicarEtapas(res.config.etapas || []);
        } else {
          this.isFalhaConfig = true;
          this.alertService.show(
            'Aviso',
            res.config?.errorDescription ||
              'Não foi possível carregar as etapas do Kanban. Tente novamente em alguns instantes.',
            AlertType.warning
          );
        }
      },
      error: () => {
        this.isLoading = false;
        this.isFalhaConfig = true;
        this.alertService.show(
          'Aviso',
          'Não foi possível carregar as etapas do Kanban. Tente novamente em alguns instantes.',
          AlertType.warning
        );
      },
    });
  }

  private recarregarConfig(): void {
    this.configKanbanService.get().subscribe({
      next: res => {
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }
        this.aplicarEtapas(res.etapas || []);
      },
      error: () =>
        this.alertService.show(
          'Aviso',
          'Não foi possível atualizar as etapas do Kanban. Recarregue a tela em alguns instantes.',
          AlertType.warning
        ),
    });
  }

  private aplicarEtapas(etapas: EtapaKanbanStruct[]): void {
    this.isFalhaConfig = false;
    this.etapas = etapas;
    this.fluxo = etapas.filter(e => e.idTipoEtapaKanban === TipoEtapaKanbanEnum.FluxoPrincipal);
    this.especiais = etapas.filter(e => e.idTipoEtapaKanban === TipoEtapaKanbanEnum.Especial);
    this.avisos = this.calcularAvisos();
  }

  private calcularAvisos(): AvisoFluxo[] {
    const avisos: AvisoFluxo[] = [];
    const destinosRecebidos = new Set(
      this.fluxo.flatMap(e => e.movimentacoes).map(m => m.idEtapaKanbanDestino)
    );

    if (!this.fluxo.some(e => e.isInicial)) {
      avisos.push({ idEtapaKanban: null, mensagem: 'O processo Ordem de serviço não tem etapa inicial.' });
    }
    if (!this.fluxo.some(e => e.isFinal) && this.especiais.length === 0) {
      avisos.push({ idEtapaKanban: null, mensagem: 'O processo Ordem de serviço não tem etapa final.' });
    }
    for (const e of this.fluxo) {
      if (!e.isInicial && !destinosRecebidos.has(e.idEtapaKanban)) {
        avisos.push({
          idEtapaKanban: e.idEtapaKanban,
          mensagem: `"${e.nome}" é inalcançável - nenhuma etapa a tem como destino e ela não é inicial.`,
        });
      }
      if (!e.isFinal && e.movimentacoes.length === 0) {
        avisos.push({
          idEtapaKanban: e.idEtapaKanban,
          mensagem: `"${e.nome}" não tem saída - não é final e não tem destinos configurados.`,
        });
      }
      if (
        e.isFinal &&
        e.movimentacoes.some(m => this.fluxo.some(f => f.idEtapaKanban === m.idEtapaKanbanDestino))
      ) {
        avisos.push({
          idEtapaKanban: e.idEtapaKanban,
          mensagem: `"${e.nome}" é final, mas ainda tem saídas configuradas para etapas do fluxo.`,
        });
      }
    }
    for (const e of this.especiais) {
      if (!destinosRecebidos.has(e.idEtapaKanban)) {
        avisos.push({
          idEtapaKanban: e.idEtapaKanban,
          mensagem: `"${e.nome}" é inalcançável - nenhuma etapa do fluxo tem esta etapa especial como destino.`,
        });
      }
    }
    return avisos;
  }

  public focarAviso(aviso: AvisoFluxo): void {
    if (!aviso.idEtapaKanban) return;
    const el = document.getElementById('etapa-kanban-' + aviso.idEtapaKanban);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('etapa-card-highlight');
    setTimeout(() => el.classList.remove('etapa-card-highlight'), 1500);
  }

  public cor(idKanbanCor: number): KanbanCor | undefined {
    return this.coresById.get(idKanbanCor);
  }

  public iconeNome(idKanbanIcone: number): string {
    return this.iconesById.get(idKanbanIcone) ?? 'label';
  }

  private aplicarAutomacoes(config: AutomacaoOrdemServicoConfig): void {
    const gatilhos: [number | null, AutomacaoDaEtapa][] = [
      [
        config.idEtapaKanbanEmissaoNFe,
        { nome: 'Emitir NF-e', icone: 'receipt_long', isAtivo: config.isAtivoEmissaoNFe },
      ],
      [
        config.idEtapaKanbanEmissaoNFSe,
        { nome: 'Emitir NFS-e', icone: 'description', isAtivo: config.isAtivoEmissaoNFSe },
      ],
      [
        config.idEtapaKanbanFinanceiro,
        { nome: 'Gerar financeiro', icone: 'payments', isAtivo: config.isAtivoFinanceiro },
      ],
      [
        config.idEtapaKanbanMovimentacaoEstoque,
        { nome: 'Movimentar estoque', icone: 'inventory_2', isAtivo: config.isAtivoMovimentacaoEstoque },
      ],
      [
        config.idEtapaKanbanReservaEstoque,
        { nome: 'Reservar estoque', icone: 'bookmark_added', isAtivo: config.isAtivoReservaEstoque },
      ],
    ];
    this.automacoesPorEtapa = new Map();
    gatilhos.forEach(([idEtapaKanban, automacao]) => {
      if (idEtapaKanban == null) return;
      this.automacoesPorEtapa.set(idEtapaKanban, [
        ...(this.automacoesPorEtapa.get(idEtapaKanban) ?? []),
        automacao,
      ]);
    });
  }

  public automacoesDaEtapa(etapa: EtapaKanbanStruct): AutomacaoDaEtapa[] {
    return this.automacoesPorEtapa.get(etapa.idEtapaKanban) ?? [];
  }

  public tooltipAutomacoes(etapa: EtapaKanbanStruct): string {
    return (
      'Automações com gatilho nesta etapa: ' +
      this.automacoesDaEtapa(etapa)
        .map(a => a.nome)
        .join(' · ') +
      '.'
    );
  }

  public tooltipVisibilidade(etapa: EtapaKanbanStruct): string {
    if (!etapa.idsPerfilVisibilidade.length) return 'Visível para todos os perfis.';
    const nomes = this.perfis.filter(p => etapa.idsPerfilVisibilidade.includes(p.idPerfil)).map(p => p.nome);
    return 'Visível para: ' + nomes.join(', ') + '.';
  }

  public tooltipMotivo(etapa: EtapaKanbanStruct): string {
    return etapa.idsMotivoTransicao.length
      ? `${etapa.idsMotivoTransicao.length} motivo(s) escolhido(s) para esta etapa.`
      : 'Todos os motivos ativos ficam disponíveis.';
  }

  public tooltipCampos(etapa: EtapaKanbanStruct): string {
    const nomes = this.camposAdicionais
      .filter(c => etapa.idsCampoAdicional.includes(c.idCampoAdicional))
      .map(c => c.nome);
    return 'Obrigatórios ao salvar nesta etapa: ' + nomes.join(', ') + '.';
  }

  public labelCampos(etapa: EtapaKanbanStruct): string {
    const qtd = etapa.idsCampoAdicional.length;
    return `${qtd} ${qtd === 1 ? 'campo obrigatório' : 'campos obrigatórios'}`;
  }

  public toggleAtivo(etapa: EtapaKanbanStruct, evento: MatSlideToggleChange): void {
    this.idEtapaProcessando = etapa.idEtapaKanban;
    this.configKanbanService.toggleAtivo(etapa.idEtapaKanban, { isAtivo: evento.checked }).subscribe({
      next: r => {
        this.idEtapaProcessando = null;
        if (r.isError) {
          evento.source.checked = etapa.isAtivo;
          this.alertService.show('Aviso', r.errorDescription, AlertType.warning);
          return;
        }
        etapa.isAtivo = evento.checked;
        this.alertService.show(
          'Sucesso',
          `Etapa "${etapa.nome}" ${evento.checked ? 'ativada' : 'desativada'}.`,
          AlertType.success
        );
      },
      error: () => {
        this.idEtapaProcessando = null;
        evento.source.checked = etapa.isAtivo;
        this.alertService.show(
          'Aviso',
          'Não foi possível alterar a situação da etapa agora. Tente novamente em alguns instantes.',
          AlertType.warning
        );
      },
    });
  }

  public iniciarArraste(): void {
    this.fixoSobrevoado = null;
  }

  public drop(event: CdkDragDrop<EtapaKanbanStruct[]>): void {
    if (event.previousIndex === event.currentIndex) {
      if (this.fixoSobrevoado) {
        this.alertService.show(
          'Aviso',
          `"${this.fixoSobrevoado.nome}" é uma etapa fixa: a posição no fluxo não pode ser alterada.`,
          AlertType.warning
        );
      }
      return;
    }

    const moveis = this.fluxo.filter(e => !e.isFixo);
    moveItemInArray(
      moveis,
      moveis.indexOf(this.fluxo[event.previousIndex]),
      moveis.indexOf(this.fluxo[event.currentIndex])
    );
    let indiceMovel = 0;
    const fluxo = this.fluxo.map(e => (e.isFixo ? e : moveis[indiceMovel++]));
    const etapas = [...fluxo, ...this.especiais];
    this.aplicarEtapas(etapas);
    this.configKanbanService.reordenar({ idsOrdenados: etapas.map(e => e.idEtapaKanban) }).subscribe({
      next: r => {
        if (r.isError) {
          this.alertService.show('Aviso', r.errorDescription, AlertType.warning);
          this.recarregarConfig();
          return;
        }
        this.alertService.show('Sucesso', 'Ordem das etapas salva.', AlertType.success);
      },
      error: () => {
        this.alertService.show(
          'Aviso',
          'Não foi possível salvar a nova ordem das etapas. Tente novamente em alguns instantes.',
          AlertType.warning
        );
        this.recarregarConfig();
      },
    });
  }

  public novaEtapa(): void {
    this.abrirModalEtapa(null);
  }

  public editar(etapa: EtapaKanbanStruct): void {
    this.abrirModalEtapa(etapa);
  }

  private abrirModalEtapa(etapa: EtapaKanbanStruct | null): void {
    const ref = this.dialog.open(EtapaKanbanOrdemServicoModalComponent, {
      width: '880px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      autoFocus: false,
      data: {
        etapa,
        etapas: this.etapas,
        cores: this.cores,
        icones: this.icones,
        perfis: this.perfis,
        camposAdicionais: this.camposAdicionais,
      },
    });

    ref.afterClosed().subscribe((result: EtapaKanbanOrdemServicoModalResult) => {
      if (!result) return;
      if (result.action === 'saved') {
        this.recarregarConfig();
      } else if (result.action === 'delete') {
        this.excluir(result.etapa);
      }
    });
  }

  public excluir(etapa: EtapaKanbanStruct): void {
    if (etapa.isFixo) {
      this.alertService.show(
        'Aviso',
        `"${etapa.nome}" é uma etapa fixa e não pode ser excluída.`,
        AlertType.warning
      );
      return;
    }

    if (etapa.isInicial) {
      this.alertService.show(
        'Aviso',
        `"${etapa.nome}" é a etapa inicial de Ordem de serviço. Marque outra etapa como inicial antes de excluí-la.`,
        AlertType.warning
      );
      return;
    }

    if (etapa.idTipoEtapaKanban === TipoEtapaKanbanEnum.FluxoPrincipal && this.fluxo.length <= 1) {
      this.alertService.show(
        'Aviso',
        `O processo Ordem de serviço precisa de ao menos uma etapa no fluxo principal. Crie outra etapa antes de excluir "${etapa.nome}".`,
        AlertType.warning
      );
      return;
    }

    const qtdMovimentacoesApontando = this.etapas.filter(e =>
      e.movimentacoes.some(m => m.idEtapaKanbanDestino === etapa.idEtapaKanban)
    ).length;

    const ref = this.dialog.open(EtapaKanbanOrdemServicoExcluirDialogComponent, {
      width: '580px',
      maxWidth: '95vw',
      autoFocus: false,
      data: {
        etapa,
        qtdMovimentacoesApontando,
        automacoes: this.automacoesDaEtapa(etapa),
      },
    });

    ref.afterClosed().subscribe((result?: EtapaKanbanOrdemServicoExcluirDialogResult) => {
      if (!result?.confirm) return;
      this.executarExclusao(etapa);
    });
  }

  private executarExclusao(etapa: EtapaKanbanStruct): void {
    this.idEtapaProcessando = etapa.idEtapaKanban;
    this.configKanbanService.excluirEtapa({ idEtapaKanban: etapa.idEtapaKanban }).subscribe({
      next: r => {
        this.idEtapaProcessando = null;
        if (r.isError) {
          this.alertService.show('Aviso', r.errorDescription, AlertType.warning);
          return;
        }
        this.alertService.show('Sucesso', `Etapa "${etapa.nome}" excluída.`, AlertType.success);
        this.recarregarConfig();
      },
      error: () => {
        this.idEtapaProcessando = null;
        this.alertService.show(
          'Aviso',
          'Não foi possível excluir a etapa agora. Tente novamente em alguns instantes.',
          AlertType.warning
        );
      },
    });
  }

  public voltar(): void {
    this.router.navigate([this.rotaVoltar]);
  }
}
